import { createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, vShow as _vShow, withDirectives as _withDirectives } from "vue";
const _hoisted_1 = {
  key: 0,
  class: "sticky-add-new"
};
import ListsService from '@/services/ListsService';
import { onMounted, ref, watch } from 'vue';
import LlIcon from '@/components/LlIcon.vue';
export default {
  __name: 'BillableTypePicker',
  props: {
    horizontal: {
      type: Boolean,
      required: false,
      default: false
    },
    form: {
      type: Object,
      required: false,
      default: () => ({})
    },
    fixedFeeList: {
      type: Array,
      required: false,
      default: () => []
    },
    entryBillingTypes: {
      type: Array,
      required: false,
      default: () => []
    },
    isFromDraft: {
      type: Boolean,
      default: false
    },
    groupType: {
      type: String,
      default: ''
    },
    hideDetails: {
      type: Boolean,
      required: false,
      default: true
    },
    showAddFixedFeeModel: {
      type: Boolean,
      default: false
    }
  },
  emits: ['billingTypeChanged', 'update:form', 'addNewFixedFeeTemplate'],
  setup(__props, {
    emit: __emit
  }) {
    const listsService = new ListsService();
    const emit = __emit;
    const props = __props;

    // Local state to avoid mutating props directly
    const localBillingType = ref(props.form.billingType || null);
    const localCharge = ref(props.form.charge || null);
    const fixedFeeSearchText = ref('');

    // Watch for external changes to form
    watch(() => props.form.billingType, newVal => {
      if (newVal && newVal !== localBillingType.value) {
        localBillingType.value = newVal;
      }
    });
    watch(() => props.form.charge, (newVal, oldVal) => {
      // Always update if newVal is different from current localCharge
      // This handles cases where a new fixed fee is created and needs to be selected
      if (newVal !== localCharge.value) {
        localCharge.value = newVal;
      }
    }, {
      immediate: true,
      deep: true
    });

    // Handle billing type change
    const handleBillingTypeChange = value => {
      localBillingType.value = value;
      updateFormBillingType(value);
      if (value?.id === 'fixedFee') {
        // Reset charge when switching to fixed fee (unless in fixed fee context)
        if (!(props.isFromDraft && props.groupType === 'fixedFeeEntries')) {
          localCharge.value = undefined;
          updateCharge(undefined);
        }
      }
    };

    // Handle charge change
    const handleChargeChange = value => {
      localCharge.value = value;
      updateCharge(value);
    };

    // Update form billing type without mutating props directly
    const updateFormBillingType = billingType => {
      if (!billingType?.id) return;
      const updatedForm = {
        ...props.form
      };
      updatedForm.billingType = billingType;
      emit('update:form', updatedForm);
    };

    // Update charge without mutating props directly
    const updateCharge = charge => {
      const updatedForm = {
        ...props.form
      };
      updatedForm.charge = charge;
      emit('update:form', updatedForm);
    };

    // Handle creating new fixed fee template
    const addNewFixedFeeTemplate = () => {
      emit('addNewFixedFeeTemplate', fixedFeeSearchText.value);
    };

    // Set up the billing type based on group type
    const setupBillingTypeFromGroupType = () => {
      // Only do this when the component is used in a draft
      if (!props.isFromDraft || !props.groupType) return;
      let billingTypeId = 'billable'; // Default

      // Map group types to billing types
      switch (props.groupType) {
        case 'fixedFeeEntries':
          billingTypeId = 'fixedFee';
          break;
        case 'nonBillableEntries':
          billingTypeId = 'nonBillable';
          break;
        case 'billableEntries':
          billingTypeId = 'billable';
          break;
        default:
          billingTypeId = 'billable';
          break;
      }

      // Find the billing type
      const billingType = props.entryBillingTypes.find(item => item.id === billingTypeId);
      if (billingType) {
        // Update local state
        localBillingType.value = billingType;

        // Update form via event
        updateFormBillingType(billingType);

        // If fixed fee and we have a list with only one item, auto-select it
        if (billingTypeId === 'fixedFee' && props.fixedFeeList?.length === 1) {
          const fixedFee = props.fixedFeeList[0];
          localCharge.value = fixedFee;
          updateCharge(fixedFee);
        }
      }
    };
    onMounted(() => {
      // Initialize local state from props
      if (props.form.billingType) {
        if (typeof props.form.billingType === 'string' || !props.form.billingType.id) {
          // Handle string billing type values
          const billingType = props.entryBillingTypes.find(item => item.id === props.form.billingType);
          if (billingType) {
            localBillingType.value = billingType;
            updateFormBillingType(billingType);
          }
        } else {
          localBillingType.value = props.form.billingType;
        }
      }
      if (props.form.charge) {
        localCharge.value = props.form.charge;
      }
      if (props.isFromDraft && props.groupType) {
        // Handle draft context (set up from group type)
        setupBillingTypeFromGroupType();
      }
    });
    return (_ctx, _cache) => {
      const _component_v_select = _resolveComponent("v-select");
      const _component_v_list_item_title = _resolveComponent("v-list-item-title");
      const _component_v_list_item = _resolveComponent("v-list-item");
      const _component_v_autocomplete = _resolveComponent("v-autocomplete");
      return _openBlock(), _createElementBlock("div", {
        class: _normalizeClass(["flex justify-between", __props.horizontal ? 'flex-col' : 'flex-row'])
      }, [_createElementVNode("div", {
        class: _normalizeClass(__props.horizontal ? 'w-full' : __props.isFromDraft || (localBillingType.value && localBillingType.value.id) === 'fixedFee' ? 'w-1/2 pr-2' : 'w-full')
      }, [_cache[3] || (_cache[3] = _createElementVNode("label", {
        class: "text-sm"
      }, "Billing Type", -1)), _createVNode(_component_v_select, {
        modelValue: localBillingType.value,
        "onUpdate:modelValue": [_cache[0] || (_cache[0] = $event => localBillingType.value = $event), handleBillingTypeChange],
        items: __props.entryBillingTypes,
        "item-title": "label",
        "item-value": "id",
        density: "compact",
        placeholder: "Select a Billing Type",
        "return-object": "",
        variant: "outlined",
        class: "remove-outline",
        "hide-details": __props.hideDetails,
        "data-cy": "billing-type-select",
        disabled: __props.isFromDraft && __props.groupType && __props.form.matter?.billing?.type !== 'internal' && __props.form.matter?.billing?.type !== 'contingency'
      }, null, 8, ["modelValue", "items", "hide-details", "disabled"])], 2), _withDirectives(_createElementVNode("div", {
        class: _normalizeClass([__props.horizontal ? 'pt-2 w-full' : 'w-1/2 pl-2', "tw"])
      }, [_cache[4] || (_cache[4] = _createElementVNode("label", {
        class: "text-sm"
      }, "Fixed Fee", -1)), _createVNode(_component_v_autocomplete, {
        modelValue: localCharge.value,
        "onUpdate:modelValue": [_cache[1] || (_cache[1] = $event => localCharge.value = $event), handleChargeChange],
        items: __props.fixedFeeList,
        "item-title": "displayName",
        "item-value": "value",
        density: "compact",
        placeholder: "Search Fixed Fees",
        "return-object": "",
        clearable: "",
        searchable: "",
        class: "remove-outline",
        variant: "outlined",
        "data-cy": "charge-select",
        "hide-details": __props.hideDetails,
        search: fixedFeeSearchText.value,
        "onUpdate:search": _cache[2] || (_cache[2] = $event => fixedFeeSearchText.value = $event)
      }, {
        "append-item": _withCtx(() => [__props.showAddFixedFeeModel ? (_openBlock(), _createElementBlock("div", _hoisted_1, [_createVNode(_component_v_list_item, {
          onClick: addNewFixedFeeTemplate,
          class: "cursor-pointer"
        }, {
          default: _withCtx(() => [_createVNode(_component_v_list_item_title, {
            class: "text-primary font-weight-medium"
          }, {
            default: _withCtx(() => [_createVNode(LlIcon, {
              icon: "mdi-plus",
              color: "primary",
              class: "mr-1 pb-1"
            }), _createTextVNode(" " + _toDisplayString(fixedFeeSearchText.value ? `Create New "${fixedFeeSearchText.value}"` : 'Create New Fixed Fee'), 1)]),
            _: 1
          })]),
          _: 1
        })])) : _createCommentVNode("", true)]),
        _: 1
      }, 8, ["modelValue", "items", "hide-details", "search"])], 2), [[_vShow, (localBillingType.value && localBillingType.value.id) === 'fixedFee']])], 2);
    };
  }
};